export const FLYTHROUGH_CAMERA_PRESET_OPTIONS = [
  { id: 'dolly_in', label: 'Dolly In' },
  { id: 'dolly_out', label: 'Dolly Out' },
  { id: 'dolly_left', label: 'Dolly Left' },
  { id: 'dolly_right', label: 'Dolly Right' },
  { id: 'jib_up', label: 'Jib Up' },
  { id: 'jib_down', label: 'Jib Down' },
  { id: 'static', label: 'Static' },
  { id: 'focus_shift', label: 'Focus Shift' },
];

export const FLYTHROUGH_MOTION_MODES = {
  CAMERA_PRESET: 'camera_preset',
  CAMERA_ANGLE_SEQUENCE: 'camera_angle_sequence',
  PROMPT: 'prompt',
  KEYFRAME_STITCHING: 'keyframe_stitching',
};

/** Up to 3 keyframes for FLF2V stitching; 1 keyframe for preset/prompt/angle workflows. */
export const FLYTHROUGH_MAX_KEYFRAMES = 3;
export const FLYTHROUGH_MAX_RENDER_SELECTION = FLYTHROUGH_MAX_KEYFRAMES;

/** Camera angle sequence: canonical view + up to 5 new angles. */
export const FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_MAX = 6;
export const FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_MAX_NEW = 5;

/** Max flythrough videos kept in local recent history per room image. */
export const SAVED_FLYTHROUGH_HISTORY_LIMIT = 8;

/** Max concurrent generate_flythrough final-tier tasks from Export Video. */
export const MAX_CONCURRENT_FLYTHROUGH_VIDEO_TASKS = 3;

export const FLYTHROUGH_MOTION_PROMPT_MAX_LENGTH = 3000;

export const FLYTHROUGH_MOTION_PROMPT_FIELD_IDS = {
  SCENE: 'scene',
  CAMERA_MOVEMENT: 'cameraMovement',
  MOTION_SPEED: 'motionSpeed',
  STYLE: 'style',
};

export const FLYTHROUGH_MOTION_PROMPT_FIELDS = [
  {
    id: FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.SCENE,
    label: 'Scene',
    placeholder: 'Describe the room and focal subject',
  },
  {
    id: FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.CAMERA_MOVEMENT,
    label: 'Camera movement',
    placeholder: 'e.g. slow orbit left, gentle dolly in',
  },
  {
    id: FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.MOTION_SPEED,
    label: 'Motion speed',
    placeholder: 'slow, moderate, or fast',
  },
  {
    id: FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.STYLE,
    label: 'Style',
    placeholder: 'cinematic, smooth, or dynamic',
  },
];

export const FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT = {
  [FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.SCENE]: '',
  [FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.CAMERA_MOVEMENT]: '',
  [FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.MOTION_SPEED]: '',
  [FLYTHROUGH_MOTION_PROMPT_FIELD_IDS.STYLE]: '',
};

/** @deprecated Use FLYTHROUGH_MOTION_PROMPT_FIELDS for UI placeholders. */
export const FLYTHROUGH_MOTION_PROMPT_TEMPLATE = `Scene: [describe the room and focal subject]
Camera movement: [e.g. slow orbit left, gentle dolly in]
Motion speed: [slow / moderate / fast]
Style: [cinematic / smooth / dynamic]`;

export const FLYTHROUGH_ROI_MODE_OPTIONS = [
  { id: 'custom', label: 'Custom' },
  { id: 'full_screen', label: 'Full Screen' },
];

export const FLYTHROUGH_RESOLUTION_OPTIONS = [
  { id: '432p', label: '432p' },
  { id: '576p', label: '576p' },
  { id: '720p', label: '720p' },
  { id: '1080p', label: '1080p' },
];

export const FLYTHROUGH_FRAME_RATE_OPTIONS = [
  { id: '24', label: '24 FPS' },
  { id: '25', label: '25 FPS' },
  { id: '48', label: '48 FPS' },
  { id: '50', label: '50 FPS' },
];

/** Preview tier always uses 24 FPS regardless of user selection. */
export const FLYTHROUGH_PREVIEW_FPS = 24;

/** Preview tier max resolution (sent as numeric height). */
export const FLYTHROUGH_PREVIEW_RESOLUTION = 768;

/** Preview tier always uses 4 seconds regardless of user selection. */
export const FLYTHROUGH_PREVIEW_DURATION_SECONDS = 4;

const FLYTHROUGH_RESOLUTION_API_VALUES = {
  '432p': 768,
  '576p': 768,
  '720p': 1024,
  '1080p': 1280,
};

const FLYTHROUGH_FRAME_RATE_API_VALUES = {
  '24': 24,
  '25': 25,
  '48': 48,
  '50': 50,
};

export function resolveFlythroughResolutionApiValue(resolutionId) {
  return FLYTHROUGH_RESOLUTION_API_VALUES[resolutionId] ?? FLYTHROUGH_PREVIEW_RESOLUTION;
}

export function resolveFlythroughResolutionIdFromApiValue(apiValue) {
  const numeric = Number(apiValue);
  const match = Object.entries(FLYTHROUGH_RESOLUTION_API_VALUES).find(([, value]) => value === numeric);
  return match?.[0] ?? '432p';
}

export function resolveFlythroughFpsApiValue(frameRateId) {
  return FLYTHROUGH_FRAME_RATE_API_VALUES[frameRateId] ?? FLYTHROUGH_PREVIEW_FPS;
}

export const FLYTHROUGH_DURATION_MIN_SECONDS = 1;
export const FLYTHROUGH_DURATION_MAX_SECONDS = 4;
export const FLYTHROUGH_DURATION_DEFAULT_SECONDS = 4;
export const FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_SECONDS_PER_ANGLE = 4;
export const FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_DURATION_MAX_SECONDS =
  FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_MAX_NEW * FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_SECONDS_PER_ANGLE;
export const FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_PREVIEW_MAX_NEW_ANGLES = 2;
export const FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_PREVIEW_DISABLED_TITLE =
  'Preview generation is only for less than 3 new camera angles';

export function getFlythroughCameraAngleSequenceAddedCount(angleCount) {
  const totalAngles = Math.max(1, Math.round(Number(angleCount) || 1));
  return Math.max(0, totalAngles - 1);
}

export function isFlythroughPreviewAllowedForCameraAngleSequence(angleCount) {
  return (
    getFlythroughCameraAngleSequenceAddedCount(angleCount) <=
    FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_PREVIEW_MAX_NEW_ANGLES
  );
}

export function getFlythroughDurationMaxSeconds(motionMode) {
  if (motionMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE) {
    return FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_DURATION_MAX_SECONDS;
  }
  return FLYTHROUGH_DURATION_MAX_SECONDS;
}

export function resolveFlythroughDurationForCameraAngleSequence(angleCount) {
  const totalAngles = Math.max(1, Math.round(Number(angleCount) || 1));
  const addedAngles = Math.max(0, totalAngles - 1);
  const seconds =
    addedAngles <= 1
      ? FLYTHROUGH_DURATION_DEFAULT_SECONDS
      : addedAngles * FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_SECONDS_PER_ANGLE;
  return Math.min(
    FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_DURATION_MAX_SECONDS,
    Math.max(FLYTHROUGH_DURATION_MIN_SECONDS, seconds),
  );
}

export function resolveFlythroughFinalDurationSeconds({
  motionMode,
  durationSeconds,
  cameraAngleSequence = [],
}) {
  if (motionMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE) {
    return resolveFlythroughDurationForCameraAngleSequence(cameraAngleSequence.length);
  }
  return clampFlythroughDurationSeconds(durationSeconds, motionMode);
}

export function clampFlythroughDurationSeconds(seconds, motionMode) {
  const value = Math.round(Number(seconds));
  if (!Number.isFinite(value)) return FLYTHROUGH_DURATION_DEFAULT_SECONDS;
  const max = motionMode
    ? getFlythroughDurationMaxSeconds(motionMode)
    : FLYTHROUGH_DURATION_MAX_SECONDS;
  return Math.min(max, Math.max(FLYTHROUGH_DURATION_MIN_SECONDS, value));
}

export const FLYTHROUGH_ROI_DEFAULT = {
  x: 0.25,
  y: 0.25,
  width: 0.25,
  height: 0.25,
};

export const FLYTHROUGH_ROI_FULL_SCREEN = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
};

export const FLYTHROUGH_ROI_MIN_SIZE = 0.05;

export function clampFlythroughRoi(roi) {
  const minSize = FLYTHROUGH_ROI_MIN_SIZE;
  let x = Math.max(0, Math.min(1, Number(roi?.x) || 0));
  let y = Math.max(0, Math.min(1, Number(roi?.y) || 0));
  let width = Math.max(minSize, Math.min(1, Number(roi?.width) || minSize));
  let height = Math.max(minSize, Math.min(1, Number(roi?.height) || minSize));

  if (x + width > 1) {
    x = Math.max(0, 1 - width);
  }
  if (y + height > 1) {
    y = Math.max(0, 1 - height);
  }

  return { x, y, width, height };
}

export function getFlythroughCameraPresetLabel(presetId) {
  return (
    FLYTHROUGH_CAMERA_PRESET_OPTIONS.find((option) => option.id === presetId)?.label ?? '—'
  );
}

export function getFlythroughResolutionLabel(resolutionId) {
  return FLYTHROUGH_RESOLUTION_OPTIONS.find((option) => option.id === resolutionId)?.label ?? '—';
}

export function getFlythroughFrameRateLabel(frameRateId) {
  return FLYTHROUGH_FRAME_RATE_OPTIONS.find((option) => option.id === frameRateId)?.label ?? '—';
}

export function formatFlythroughRoiLabel(roiMode, roi) {
  if (roiMode === 'full_screen') return 'Full Screen';
  const safeRoi = clampFlythroughRoi(roi);
  return `Custom (${safeRoi.x.toFixed(2)}, ${safeRoi.y.toFixed(2)}, ${safeRoi.width.toFixed(2)}, ${safeRoi.height.toFixed(2)})`;
}
